import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { useTranslation } from 'react-i18next';

import CodeSlashIcon from 'assets/images/icons/code-slash.svg?react';
import Button from 'components/button/Button';
import { RichTextEditorToolbarPluginProps } from 'components/editor/RichTextEditor.types';
import { setEditorContent } from 'components/editor/RichTextEditor.utils';

interface ToggleEditHTMLModeButtonProps {
  disabled: boolean;
  htmlEditorRef: RichTextEditorToolbarPluginProps['htmlEditorRef'];
  isHTMLEditModeEnabled: RichTextEditorToolbarPluginProps['isHTMLEditModeEnabled'];
  setHTMLEditModeEnabled: RichTextEditorToolbarPluginProps['setHTMLEditModeEnabled'];
  toolbarSettings: RichTextEditorToolbarPluginProps['toolbarSettings'];
}

const ToggleEditHTMLModeButton = ({
  disabled,
  htmlEditorRef,
  isHTMLEditModeEnabled,
  setHTMLEditModeEnabled,
  toolbarSettings: { features },
}: ToggleEditHTMLModeButtonProps) => {
  const { t } = useTranslation();
  const [editor] = useLexicalComposerContext();

  if (!features?.editHTML) {
    return null;
  }

  const handleOnClick = () => {
    // If the user is toggling HTML edit mode off, we need to update lexical's
    // editor state.
    if (isHTMLEditModeEnabled) {
      editor.update(() => {
        if (htmlEditorRef.current) {
          setEditorContent({ editor, contents: htmlEditorRef.current.value });
        }
      });
    }

    setHTMLEditModeEnabled(!isHTMLEditModeEnabled);
  };

  return (
    <Button
      aria-label={t('form.editor.buttons.editHTML')}
      className={`toolbar-item ${isHTMLEditModeEnabled ? 'active' : ''}`}
      data-testid="toolbar-button-editHTML"
      disabled={disabled}
      onClick={handleOnClick}
    >
      <CodeSlashIcon />
    </Button>
  );
};

export default ToggleEditHTMLModeButton;
